//frontend/src/components/dashboard/TabsSettings.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle, 
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Alert,
  Divider,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axios';
import { StrictMode } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const TabsSettings = ({ open, onClose, isAdmin }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
      hiddenTabs: {
          appointments: false,
          specialists: false,
          services: false,
          clients: false,
          products: false
      },
      columnSettings: {
          showNameCompany: true,
          showFirstContact: true,
          showContacts: true,
          showCreatedBy: true,
          showAssignedTo: true,
          showStatus: true,
          showProjectType: true
      },
      tabOrder: [
          'appointments',
          'specialists',
          'services', 
          'clients',
          'products',
          'settings'
      ]
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
      if (open) {
          fetchSettings();
      }
  }, [open]);

  const fetchSettings = async () => {
      try {
          const response = await axiosInstance.get('/settings/tabs');
          setSettings({
              hiddenTabs: response.data.hiddenTabs || {},
              columnSettings: response.data.columnSettings || {},
              tabOrder: response.data.tabOrder || [
                  'appointments',
                  'specialists',
                  'services',
                  'clients',
                  'products',
                  'settings'
              ]
          });
          setError(null);
      } catch (error) {
          setError(t('settings.errorFetching'));
          console.error('Error fetching settings:', error);
      }
  };

  const handleTabChange = (tab) => (event) => {
      setSettings(prev => ({
          ...prev,
          hiddenTabs: {
              ...prev.hiddenTabs,
              [tab]: event.target.checked
          }
      }));
  };

  const handleColumnSettingChange = (setting, checked) => {
      setSettings(prev => ({
          ...prev,
          columnSettings: {
              ...prev.columnSettings,
              [setting]: checked
          }
      }));
  };

  const handleDragEnd = (result) => {
      if (!result.destination) return;

      const items = Array.from(settings.tabOrder);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      setSettings(prev => ({
          ...prev,
          tabOrder: items
      }));
  };

  const handleSave = async () => {
      try {
          await axiosInstance.patch('/settings/tabs', settings);
          setSuccess(t('settings.savedSuccessfully'));
          setTimeout(() => {
              onClose();
              window.location.reload();
          }, 1500);
      } catch (error) {
          setError(t('settings.errorSaving'));
      }
  };

  return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle>{t('settings.tabAndColumnVisibility')}</DialogTitle>
          <DialogContent sx={{ overflow: 'hidden' }}>
              <Box sx={{ overflowY: 'auto', maxHeight: '80vh', p: 2 }}>
                  {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                  {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                  <Typography variant="h6" gutterBottom>
                      {t('settings.tabOrder')}
                  </Typography>

                  <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="droppable-tabs">
                          {(provided) => (
                              <Box
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                  sx={{ mb: 3 }}
                              >
                                  {settings.tabOrder.map((tab, index) => (
                                      <Draggable key={tab} draggableId={tab} index={index}>
                                          {(provided) => (
                                              <Box
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  sx={{
                                                      p: 2,
                                                      mb: 1,
                                                      bgcolor: 'background.default',
                                                      border: '1px solid',
                                                      borderColor: 'divider',
                                                      borderRadius: 1,
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'space-between'
                                                  }}
                                              >
                                                  <Typography>{t(`dashboard.tabs.${tab}`)}</Typography>
                                                  <div {...provided.dragHandleProps}>
                                                      <IconButton size="small">
                                                          <DragIndicatorIcon />
                                                      </IconButton>
                                                  </div>
                                              </Box>
                                          )}
                                      </Draggable>
                                  ))}
                                  {provided.placeholder}
                              </Box>
                          )}
                      </Droppable>
                  </DragDropContext>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="h6" gutterBottom>
                      {t('settings.hiddenTabs')}
                  </Typography>
                  <FormGroup>
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.hiddenTabs.appointments || false}
                                  onChange={handleTabChange('appointments')}
                              />
                          }
                          label={t('settings.hideAppointments')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.hiddenTabs.specialists || false}
                                  onChange={handleTabChange('specialists')}
                              />
                          }
                          label={t('settings.hideSpecialists')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.hiddenTabs.services || false}
                                  onChange={handleTabChange('services')}
                              />
                          }
                          label={t('settings.hideServices')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.hiddenTabs.clients || false}
                                  onChange={handleTabChange('clients')}
                              />
                          }
                          label={t('settings.hideClients')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.hiddenTabs.products || false}
                                  onChange={handleTabChange('products')}
                              />
                          }
                          label={t('settings.hideProducts')}
                      />
                  </FormGroup>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="h6" gutterBottom>
                      {t('settings.clientColumns')}
                  </Typography>
                  <FormGroup>
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.columnSettings.showNameCompany || false}
                                  onChange={(e) => handleColumnSettingChange('showNameCompany', e.target.checked)}
                              />
                          }
                          label={t('common.namecompany')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.columnSettings.showFirstContact || false}
                                  onChange={(e) => handleColumnSettingChange('showFirstContact', e.target.checked)}
                              />
                          }
                          label={t('common.firstcontact')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.columnSettings.showContacts || false}
                                  onChange={(e) => handleColumnSettingChange('showContacts', e.target.checked)}
                              />
                          }
                          label={t('common.contacts')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.columnSettings.showCreatedBy || false}
                                  onChange={(e) => handleColumnSettingChange('showCreatedBy', e.target.checked)}
                              />
                          }
                          label={t('common.createdby')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.columnSettings.showAssignedTo || false}
                                  onChange={(e) => handleColumnSettingChange('showAssignedTo', e.target.checked)}
                              />
                          }
                          label={t('common.assignedto')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.columnSettings.showStatus || false}
                                  onChange={(e) => handleColumnSettingChange('showStatus', e.target.checked)}
                              />
                          }
                          label={t('common.status')}
                      />
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={settings.columnSettings.showProjectType || false}
                                  onChange={(e) => handleColumnSettingChange('showProjectType', e.target.checked)}
                              />
                          }
                          label={t('common.projectType')}
                      />
                  </FormGroup>
              </Box>
          </DialogContent>
          <DialogActions>
   <Button 
       onClick={onClose} 
       tabIndex={0}
   >
       {t('common.cancel')}
   </Button>
   <Button 
       onClick={handleSave} 
       variant="contained"
       tabIndex={0}
   >
       {t('common.save')}
   </Button>
</DialogActions>
      </Dialog>
  );
};

export default TabsSettings;